import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Switch from '../../../components/ui/Switch';
import type { ProfilePersonaInput } from '../../../api/PublicProfile/publicProfileApi';

interface AddPersonaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (input: ProfilePersonaInput) => void;
}

interface FormData {
  name: string;
  description: string;
  isDefault: boolean;
  requiresApproval: boolean;
  allowSlugRegeneration: boolean;
}

interface Errors {
  name?: string;
  description?: string;
}

const AddPersonaModal: React.FC<AddPersonaModalProps> = ({ isOpen, onClose, onAdd }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    isDefault: false,
    requiresApproval: false,
    allowSlugRegeneration: true,
  });

  const [errors, setErrors] = useState<Errors>({});

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors?.[field as keyof Errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Errors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = t('Persona name is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (validateForm()) {
      const input: ProfilePersonaInput = {
        name: formData.name,
        description: formData.description || undefined,
        isDefault: formData.isDefault,
        requiresApproval: formData.requiresApproval,
        allowSlugRegeneration: formData.allowSlugRegeneration,
      };

      onAdd(input);
      setFormData({
        name: '',
        description: '',
        isDefault: false,
        requiresApproval: false,
        allowSlugRegeneration: true,
      });
      setErrors({});
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      isDefault: false,
      requiresApproval: false,
      allowSlugRegeneration: true,
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent size="default">
        <DialogHeader>
          <DialogTitle>{t('Add New Persona')}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Persona Name')} *
              </label>
              <input
                type="text"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                placeholder={t('Enter persona name')}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              {errors?.name && (
                <p className="text-sm text-destructive mt-1">{errors?.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('Description')}
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder={t('Enter persona description')}
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>

            {/* Toggle Options */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.isDefault}
                  onCheckedChange={(checked) => handleInputChange('isDefault', checked)}
                  label={t('Set as Default')}
                  description={t('New profiles will use this persona by default')}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.requiresApproval}
                  onCheckedChange={(checked) => handleInputChange('requiresApproval', checked)}
                  label={t('Requires HR Approval')}
                  description={t('Profiles must be approved before going public')}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <Switch
                  checked={formData.allowSlugRegeneration}
                  onCheckedChange={(checked) => handleInputChange('allowSlugRegeneration', checked)}
                  label={t('Allow Link Regeneration')}
                  description={t('Users can regenerate their profile URL')}
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="flex items-start gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <p className="text-xs text-primary">
                {t('Personas define the configuration templates for employee public profiles. Each persona can have different visibility rules, approval workflows, and branding options.')}
              </p>
            </div>
          </form>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {t('Cancel')}
          </Button>
          <Button variant="default" onClick={handleSubmit}>
            {t('Add Persona')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddPersonaModal;
